import { useEffect, useState, type ReactNode } from 'react';
import { apiFetch, descargarArchivoPost } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { Ayuda } from '../../components/Ayuda';

/**
 * Admin → Formato Documentos (2026-08-11): formato visual por defecto de TODOS los capítulos
 * generados (el Word oficial). Configura fuente, cuerpo, títulos, subtítulos, epígrafes, tablas,
 * imágenes, índices y numeración automática de títulos. Se guarda en app_settings.formato_documento
 * y lo lee el renderizador. Incluye "Descargar Word de ejemplo" (previsualiza sin guardar).
 */
interface Formato {
  fuente: string;
  tamCuerpo: number; colorTexto: string; negritaCuerpo: boolean;
  tamTitulo: number; colorTitulo: string; negritaTitulo: boolean;
  tamSubtitulo: number; colorSubtitulo: string; negritaSubtitulo: boolean;
  tamEpigrafe: number; colorEpigrafe: string;
  tamTabla: number; tablaEncabezadoNegrita: boolean; tablaColorEncabezado: string; tablaColorBordes: string;
  imagenAnchoPct: number; imagenCentrada: boolean; imagenBordesRedondeados: boolean;
  interlineado: number; justificado: boolean;
  indiceContenido: boolean; indiceTablas: boolean; indiceFiguras: boolean; indiceNiveles: number;
  numerarTitulos: boolean;
  mostrarFuentes?: boolean; // marcas de procedencia [Fn] en el Word (2026-10-19); default false
}
const hash = (h: string) => (h.startsWith('#') ? h : `#${h}`);

// Sub-componentes a nivel de MÓDULO (no dentro del render → los inputs no pierden el foco al escribir).
// `tip` = ⓘ explicativo (componente Ayuda: hover/foco en desktop, tap en móvil).
function Info({ tip }: { tip?: string }) {
  if (!tip) return null;
  return <Ayuda texto={tip} className="ml-0.5" />;
}
function Seccion({ titulo, tip, children }: { titulo: string; tip?: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#e6eef8] bg-white p-5 shadow-sm">
      <h2 className="mb-3 flex items-center text-[13px] font-semibold text-azul-noche">{titulo}<Info tip={tip} /></h2>
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">{children}</div>
    </div>
  );
}
function Campo({ label, hint, tip, children }: { label: string; hint?: string; tip?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center text-[11.5px] font-medium text-gris">{label}<Info tip={tip} /></span>
      {children}
      {hint && <span className="max-w-[180px] text-[10.5px] font-light leading-snug text-gris/80">{hint}</span>}
    </label>
  );
}
function NumIn({ label, hint, tip, value, onChange, min = 6, max = 72 }: { label: string; hint?: string; tip?: string; value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <Campo label={label} hint={hint} tip={tip}>
      <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="w-20 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-azul-noche focus:border-azul-profundo focus:outline-none" />
    </Campo>
  );
}
function ColIn({ label, tip, value, onChange }: { label: string; tip?: string; value: string; onChange: (v: string) => void }) {
  return (
    <Campo label={label} tip={tip}>
      <span className="flex items-center gap-2">
        <input type="color" value={hash(value)} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 cursor-pointer rounded border border-slate-200" />
        <span className="text-[11px] text-gris">{hash(value)}</span>
      </span>
    </Campo>
  );
}
function SwIn({ label, tip, value, onChange }: { label: string; tip?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <Campo label={label} tip={tip}>
      <button type="button" role="switch" aria-checked={value} onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-azul-profundo' : 'bg-slate-300'}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </Campo>
  );
}

export default function FormatoDocumentos() {
  const [form, setForm] = useState<Formato | null>(null);
  const [def, setDef] = useState<Formato | null>(null);
  const [fuentes, setFuentes] = useState<string[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<{ formato: Formato; default: Formato; fuentes: string[] }>('/api/admin/formato-documento');
        setForm(d.formato); setDef(d.default); setFuentes(d.fuentes);
      } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo cargar el formato.'); }
      finally { setCargando(false); }
    })();
  }, []);

  const set = <K extends keyof Formato>(k: K, v: Formato[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const payload = (f: Formato) => ({
    ...f, colorTexto: hash(f.colorTexto), colorTitulo: hash(f.colorTitulo), colorSubtitulo: hash(f.colorSubtitulo),
    colorEpigrafe: hash(f.colorEpigrafe), tablaColorEncabezado: hash(f.tablaColorEncabezado), tablaColorBordes: hash(f.tablaColorBordes),
  });

  async function guardar() {
    if (!form) return;
    setGuardando(true); setAviso(null); setError(null);
    try {
      await apiFetch('/api/admin/formato-documento', { method: 'PUT', body: JSON.stringify(payload(form)) });
      setAviso('✓ Formato guardado. Se aplicará a los próximos capítulos que se generen.');
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo guardar.'); }
    finally { setGuardando(false); }
  }
  async function descargarEjemplo() {
    if (!form) return;
    setGenerando(true); setError(null);
    try { await descargarArchivoPost('/api/admin/formato-documento/ejemplo', payload(form), 'ejemplo-formato.docx'); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo generar el ejemplo.'); }
    finally { setGenerando(false); }
  }

  if (cargando) return <div className="flex min-h-64 items-center justify-center"><Spinner tamano="lg" /></div>;
  if (!form) return <div className="p-6 text-sm text-red-600">{error ?? 'No disponible.'}</div>;

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <h1 className="text-lg font-semibold tracking-tight text-azul-noche">Formato Documentos</h1>
      <p className="mt-1 text-[13px] font-light text-gris">Formato visual por defecto de todos los capítulos que generan los agentes (el Word oficial). Los títulos principales usan el estilo "Título 1" para el índice automático.</p>

      <div className="mt-5 space-y-4">
        <Seccion titulo="Fuente" tip="La tipografía de TODO el documento (cuerpo, títulos y tablas).">
          <Campo label="Fuente del documento" hint="Solo fuentes disponibles en Word." tip="Se limita a fuentes seguras de Word para que el documento se vea igual en cualquier computador. AMBEC usa Calibri.">
            <select value={form.fuente} onChange={(e) => set('fuente', e.target.value)}
              className="w-56 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-azul-noche focus:border-azul-profundo focus:outline-none">
              {fuentes.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Campo>
        </Seccion>

        <Seccion titulo="Cuerpo" tip="El texto normal de los párrafos del capítulo.">
          <NumIn label="Tamaño (pt)" tip="Tamaño de la letra del cuerpo, en puntos. AMBEC usa 11." value={form.tamCuerpo} onChange={(v) => set('tamCuerpo', v)} />
          <ColIn label="Color" tip="Color de la letra del cuerpo. AMBEC usa negro." value={form.colorTexto} onChange={(v) => set('colorTexto', v)} />
          <SwIn label="Negrita" tip="Poner TODO el cuerpo en negrita. En los informes reales de AMBEC va apagado (solo los títulos van en negrita)." value={form.negritaCuerpo} onChange={(v) => set('negritaCuerpo', v)} />
          <Campo label="Interlineado" tip="Separación entre líneas del párrafo. AMBEC usa ~1,15.">
            <select value={form.interlineado} onChange={(e) => set('interlineado', Number(e.target.value))}
              className="w-24 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-azul-noche focus:border-azul-profundo focus:outline-none">
              <option value={1}>Sencillo</option>
              <option value={1.15}>1,15</option>
              <option value={1.5}>1,5</option>
              <option value={2}>Doble</option>
            </select>
          </Campo>
          <SwIn label="Justificado" tip="Alinear el texto a ambos márgenes (como en un informe). Si se apaga, queda alineado a la izquierda." value={form.justificado} onChange={(v) => set('justificado', v)} />
        </Seccion>

        <Seccion titulo="Títulos (Título 1)" tip="Los títulos principales de sección (1, 2, 3…). Usan el estilo Word 'Título 1', necesario para el índice automático.">
          <NumIn label="Tamaño (pt)" tip="Tamaño del título principal. Si numeras los títulos, el número toma este mismo tamaño." value={form.tamTitulo} onChange={(v) => set('tamTitulo', v)} />
          <ColIn label="Color" tip="Color del título principal (texto y número). AMBEC usa negro." value={form.colorTitulo} onChange={(v) => set('colorTitulo', v)} />
          <SwIn label="Negrita" tip="Título principal en negrita. AMBEC lo usa encendido." value={form.negritaTitulo} onChange={(v) => set('negritaTitulo', v)} />
        </Seccion>

        <Seccion titulo="Subtítulos (Título 2)" tip="Los subtítulos (1.1, 1.2, 1.1.1…). Aparecen en el índice si eliges niveles 1.1.1.">
          <NumIn label="Tamaño (pt)" tip="Tamaño de los subtítulos." value={form.tamSubtitulo} onChange={(v) => set('tamSubtitulo', v)} />
          <ColIn label="Color" tip="Color de los subtítulos (texto y número)." value={form.colorSubtitulo} onChange={(v) => set('colorSubtitulo', v)} />
          <SwIn label="Negrita" tip="Subtítulos en negrita." value={form.negritaSubtitulo} onChange={(v) => set('negritaSubtitulo', v)} />
        </Seccion>

        <Seccion titulo="Epígrafes y referencias" tip="Los rótulos 'Tabla N.' / 'Figura N.' que van sobre tablas y figuras, y la sección de Referencias.">
          <NumIn label="Tamaño (pt)" tip="Tamaño de los epígrafes de tabla/figura/foto/gráfico y del texto de la sección de Referencias. AMBEC usa 10." value={form.tamEpigrafe} onChange={(v) => set('tamEpigrafe', v)} />
          <ColIn label="Color" tip="Color de los epígrafes y referencias." value={form.colorEpigrafe} onChange={(v) => set('colorEpigrafe', v)} />
        </Seccion>

        <Seccion titulo="Tablas" tip="Formato de las tablas del capítulo (encabezado, contenido y bordes).">
          <NumIn label="Contenido (pt)" tip="Tamaño de la letra dentro de las celdas. AMBEC usa 9 (más chico que el cuerpo)." value={form.tamTabla} onChange={(v) => set('tamTabla', v)} />
          <SwIn label="Encabezado en negrita" tip="Poner en negrita la fila de títulos de la tabla." value={form.tablaEncabezadoNegrita} onChange={(v) => set('tablaEncabezadoNegrita', v)} />
          <ColIn label="Relleno encabezado" tip="Color de fondo de la fila de títulos. Si es oscuro (p. ej. azul AMBEC), el texto se pone blanco automáticamente." value={form.tablaColorEncabezado} onChange={(v) => set('tablaColorEncabezado', v)} />
          <ColIn label="Color de bordes" tip="Color de las líneas/bordes de la tabla." value={form.tablaColorBordes} onChange={(v) => set('tablaColorBordes', v)} />
        </Seccion>

        <Seccion titulo="Imágenes / figuras" tip="Cómo se muestran las figuras/imágenes que insertan los agentes en el capítulo.">
          <NumIn label="Ancho máximo (%)" min={20} max={100} tip="Ancho máximo de la imagen como % del ancho útil de la página. 100% = ocupa todo el ancho." value={form.imagenAnchoPct} onChange={(v) => set('imagenAnchoPct', v)} />
          <SwIn label="Centradas" tip="Centrar las figuras en la página. Si se apaga, quedan alineadas a la izquierda." value={form.imagenCentrada} onChange={(v) => set('imagenCentrada', v)} />
          <SwIn label="Esquinas redondeadas" tip="Redondear las esquinas de las figuras para un look más moderno." value={form.imagenBordesRedondeados} onChange={(v) => set('imagenBordesRedondeados', v)} />
        </Seccion>

        <Seccion titulo="Índices" tip="Las tablas de contenido que se arman al inicio del documento.">
          <SwIn label="Índice de contenido" tip="Incluir la tabla de contenidos (los títulos y subtítulos con su página)." value={form.indiceContenido} onChange={(v) => set('indiceContenido', v)} />
          <SwIn label="Índice de tablas" tip="Incluir el listado de todas las tablas con su página." value={form.indiceTablas} onChange={(v) => set('indiceTablas', v)} />
          <SwIn label="Índice de figuras" tip="Incluir el listado de todas las figuras con su página." value={form.indiceFiguras} onChange={(v) => set('indiceFiguras', v)} />
          <Campo label="Niveles del índice" tip="Hasta qué nivel de título aparece en el índice de contenido: hasta 1.1 o hasta 1.1.1.">
            <select value={form.indiceNiveles} onChange={(e) => set('indiceNiveles', Number(e.target.value))}
              className="w-52 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-azul-noche focus:border-azul-profundo focus:outline-none">
              <option value={2}>Hasta títulos (1.1)</option>
              <option value={3}>Hasta subtítulos (1.1.1)</option>
            </select>
          </Campo>
        </Seccion>

        <Seccion titulo="Numeración" tip="Numeración automática de los títulos por parte de Word.">
          <SwIn label="Numerar títulos automáticamente" tip="Word pone y renumera solo los números de los títulos (1, 1.1, 1.1.1). Si un título ya trae número en el texto, se le quita para no duplicar. Tablas y figuras siempre se numeran solas." value={form.numerarTitulos} onChange={(v) => set('numerarTitulos', v)} />
          <span className="max-w-md text-[11px] font-light text-gris">Word numera los títulos (1, 1.1, 1.1.1) y renumera solo. Tablas y figuras ya se numeran.</span>
        </Seccion>

        <Seccion titulo="Fuentes (procedencia por dato)" tip="Cada dato del capítulo lleva una marca [Fn] que apunta a la fuente registrada por el sistema. Aquí decides si esas marcas se ven en el Word.">
          <SwIn label="Mostrar fuentes en el Word (superíndices + anexo Fuentes)" tip="Por defecto el Word de entrega va limpio. Actívalo para revisiones internas: cada dato sale con un superíndice y al final un anexo con la fuente y la fecha de consulta." value={form.mostrarFuentes ?? false} onChange={(v) => set('mostrarFuentes', v)} />
          <span className="max-w-md text-[11px] font-light text-gris">En la vista previa y en el visor del chat las marcas se ven siempre (superíndices con la fuente al tocarlos).</span>
        </Seccion>
      </div>

      {aviso && <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-700">{aviso}</div>}
      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{error}</div>}

      <div className="sticky bottom-0 mt-5 flex flex-wrap items-center gap-2.5 rounded-xl border border-[#e6eef8] bg-white/95 p-3 shadow-sm backdrop-blur">
        <button type="button" onClick={guardar} disabled={guardando}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-azul-profundo px-4 text-sm font-medium text-white transition-colors hover:bg-azul-noche disabled:opacity-50">
          {guardando ? <Spinner tamano="xs" /> : null} Guardar
        </button>
        <button type="button" onClick={descargarEjemplo} disabled={generando}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-azul-profundo/30 bg-white px-4 text-sm font-medium text-azul-profundo transition-colors hover:bg-celeste/15 disabled:opacity-50">
          {generando ? <Spinner tamano="xs" /> : '⬇'} Descargar Word de ejemplo
        </button>
        {def && (
          <button type="button" onClick={() => setForm({ ...def })} className="ml-auto text-[12.5px] font-medium text-gris hover:text-azul-noche">Restablecer al formato AMBEC</button>
        )}
      </div>
    </div>
  );
}
